import { TICKET_PAYMENT } from "../../config/global.configuration";
import { PayrexxCheckoutService } from "../../services/PayrexxCheckoutService";
import { TicketPurchaseApi } from "../../services/TicketPurchaseApi";
import type { TicketPurchaseState } from "../../store/ephemeral";
import { ephemeralStore } from "../../store/ephemeral";
import { ticketPurchaseSlice } from "../../store/ephemeral/slices/ticketPurchase.slice";
import { pricingPrefsSlice } from "../../store/persistence";
import { persistentSliceStore } from "../../store/persistence/runtime/PersistentSliceStore";
import type { PurchaseAttemptResumeResponseDTO } from "../../types/dtos/api/purchase/purchase-attempt.dto";
import type { BookingResponseDTO } from "../../types/dtos/api/purchase/purchase-booking.dto";
import type { PurchasePaymentStatusDTO } from "../../types/dtos/api/purchase/purchase-payment.dto";
import { rebuildSelectionFromPrebookings } from "./ticketPurchaseSegments.tools";

/**
 * Helpers for the Payrexx post-payment return (web page and native deep link).
 *
 * Both entry points converge on {@link resumePayrexxReturn}: verify the payment
 * through the backend (which re-checks Payrexx), reload the attempt snapshot,
 * and reopen the wizard on the matching step.
 */

/** Query parameters carried by the backend bounce (web URL or `flowride://` deep link). */
export interface PayrexxReturnParams {
    "attemptId": string | null;
    "paymentId": string | null;
    "status":    string | null;
}

/** Payment statuses that may still resolve and therefore deserve a re-check poll. */
const POLLABLE_STATUSES: ReadonlySet<string> = new Set( [ "pending" ] );

/**
 * Payment statuses meaning money is secured for the booking to run:
 * `authorized` = amount blocked on the payment method (reservation, not yet
 * charged), `succeeded` = captured.
 */
export const PAYMENT_SECURED_STATUSES: ReadonlySet<string> = new Set( [
    "authorized",
    "succeeded",
] );

/**
 * Parses the backend bounce URL parameters.
 *
 * `status` is only a display hint — it is never trusted as the payment outcome;
 * the authoritative value comes from `POST /api/purchase/payment/verify`.
 *
 * @param search - Query string (`?a=b&…`) or a full URL / `flowride://` deep link.
 * @returns Parsed parameters; every field is `null` when absent.
 */
export function parsePayrexxReturnParams( search: string ): PayrexxReturnParams {
    const queryStart = search.indexOf( "?" );
    const params = new URLSearchParams( queryStart >= 0 ? search.slice( queryStart + 1 ) : search );

    return {
        "attemptId": params.get( "attemptId" ),
        "paymentId": params.get( "paymentId" ),
        "status":    params.get( "status" ),
    };
}

/**
 * Whether a URL is the native Payrexx return deep link.
 *
 * @param url - URL received through `App` `appUrlOpen`.
 * @returns `true` when the URL starts with the configured `flowride://` return prefix.
 */
export function isPayrexxNativeReturnUrl( url: string ): boolean {
    return url.startsWith( TICKET_PAYMENT.nativeReturnUrlPrefix );
}

/**
 * Re-checks the payment against the backend until it leaves `pending` or the
 * bounded poll budget is exhausted.
 *
 * @param purchaseAttemptId - Purchase attempt id.
 * @param paymentId - Optional payment id to verify.
 * @returns The last verified payment status response, or `null` on failure.
 */
export async function verifyPaymentUntilSettled(
    purchaseAttemptId: string,
    paymentId?: string,
): Promise<PurchasePaymentStatusDTO | null> {
    for ( let pollAttempt = 0; pollAttempt < TICKET_PAYMENT.verifyPollMaxAttempts; pollAttempt++ ) {
        try {
            const response = await TicketPurchaseApi.postPurchasePaymentVerify( {
                purchaseAttemptId,
                "paymentId": paymentId,
            } );

            if ( !POLLABLE_STATUSES.has( response.status ) ) {
                return response.status;
            }

            if ( pollAttempt < TICKET_PAYMENT.verifyPollMaxAttempts - 1 ) {
                await new Promise<void>( ( resolve ) => setTimeout( resolve, TICKET_PAYMENT.verifyPollIntervalMs ) );
            }
        } catch {
            // A failed re-check must not loop; surface `null` and let the UI show the retry path.
            return null;
        }
    }

    return "pending";
}

/**
 * Cancels a still-pending payment: deletes the Payrexx gateway on the backend
 * and closes the native in-app browser. Best effort — a cancel failure only
 * means the gateway dies on its own at the end of its validity.
 *
 * @param purchaseAttemptId - Purchase attempt id owning the pending payment.
 */
export async function cancelPendingPurchasePayment( purchaseAttemptId: string ): Promise<void> {
    try {
        await TicketPurchaseApi.postPurchasePaymentCancel( { purchaseAttemptId } );
    } catch {
        // Best effort: the gateway validity still bounds its lifetime server-side.
    }

    await PayrexxCheckoutService.closeCheckout();
}

/** Attempt statuses meaning the booking already started (never call `/book` again). */
export const BOOKING_STARTED_ATTEMPT_STATUSES: ReadonlySet<string> = new Set( [
    "booking_in_progress",
    "booking_unresolved",
    "booked",
    "tickets_fetched",
] );

/**
 * Attempt statuses reporting a definitive purchase failure to the user.
 *
 * `booking_unresolved` means the synchronous booking could not be confirmed:
 * the backend never delivers those tickets — the booking is cancelled or
 * refunded server-side and the payment is released or refunded, so the attempt
 * is a user-facing failure even while compensation still runs.
 */
export const BOOKING_FAILED_ATTEMPT_STATUSES: ReadonlySet<string> = new Set( [
    "booking_failed",
    "booking_unresolved",
    "cancelled",
] );

/** Outcome of polling a booking under verification. */
export type BookingPollOutcome =
  {
      "kind":    "booked";
      "booking": BookingResponseDTO;
  } |
  { "kind": "failed" } |
  { "kind": "pending" };

/**
 * Extracts the booking result of a resume snapshot once the attempt is booked.
 *
 * @param resume - Attempt resume snapshot.
 * @returns Booking result, or `null` while the attempt is not booked.
 */
export function bookingFromResume( resume: PurchaseAttemptResumeResponseDTO ): BookingResponseDTO | null {
    if ( ( resume.status !== "booked" && resume.status !== "tickets_fetched" ) || resume.booking === null || resume.booking.providerBookingId === null ) {
        return null;
    }

    return {
        "purchaseAttemptId":   resume.purchaseAttemptId,
        "bookingId":           resume.booking.bookingId,
        "invoiceNumber":       resume.booking.invoiceNumber,
        "creditNotes":         resume.booking.creditNotes,
        "provider":            resume.booking.provider,
        "providerBookingId":   resume.booking.providerBookingId,
        "conversationId":      resume.conversationId,
        "status":              resume.status,
        "ticketFilesComplete": resume.booking.ticketFilesComplete,
        "tickets":             resume.booking.tickets,
        "paymentMethod":       resume.payment?.paymentMethod ?? null,
    };
}

/**
 * Polls the attempt while its booking runs synchronously server-side.
 *
 * Bounded by `TICKET_PAYMENT.bookingStatusPollMaxAttempts` ×
 * `bookingStatusPollIntervalMs`. A booked attempt is only a success once its
 * ticket files are stored (`tickets_fetched` / `ticketFilesComplete`) — while
 * files are still downloading the poll keeps going and the success screen
 * stays out of reach. `booking_unresolved` is a terminal user-facing failure
 * (the backend cancels or refunds it, tickets are never delivered). When the
 * budget is exhausted with emitted-but-incomplete tickets the last snapshot is
 * returned as `booked` (degraded — the next `/bookings` sync repairs it); a
 * booking still in progress is reported as `pending` — an inconclusive
 * outcome: the backend may still complete (tickets then appear in « Mes
 * billets »), otherwise the payment is released or refunded server-side.
 *
 * @param purchaseAttemptId - Purchase attempt id.
 * @returns `booked` with the tickets, `failed`, or `pending`.
 */
export async function pollBookingOutcome( purchaseAttemptId: string ): Promise<BookingPollOutcome> {
    let lastEmitted: BookingResponseDTO | null = null;

    for ( let pollAttempt = 0; pollAttempt < TICKET_PAYMENT.bookingStatusPollMaxAttempts; pollAttempt++ ) {
        if ( pollAttempt > 0 ) {
            await new Promise<void>( ( resolve ) => setTimeout( resolve, TICKET_PAYMENT.bookingStatusPollIntervalMs ) );
        }

        try {
            const resume = await TicketPurchaseApi.getPurchaseAttempt( purchaseAttemptId );
            const booking = bookingFromResume( resume );

            if ( booking !== null ) {
                if ( resume.status === "tickets_fetched" || booking.ticketFilesComplete ) {
                    return {
                        "kind": "booked",
                        booking,
                    };
                }

                // Tickets are emitted but the file pass is still running.
                lastEmitted = booking;
            }

            if ( BOOKING_FAILED_ATTEMPT_STATUSES.has( resume.status ) ) {
                return { "kind": "failed" };
            }
        } catch {
            // Transient read failure: keep polling within the budget.
        }
    }

    return lastEmitted !== null
        ? {
            "kind":    "booked",
            "booking": lastEmitted,
        }
        : { "kind": "pending" };
}

/**
 * Rebuilds the purchase wizard state after a Payrexx return.
 *
 * Loads the attempt snapshot from the backend (itinerary, prebooking, payment),
 * writes the ephemeral slice, and reopens the modal on `payment` (still
 * pending/failed) or `confirmation` (reservation secured → booking runs and
 * captures it).
 *
 * @param attemptId - Purchase attempt id from the bounce URL.
 * @param paymentStatus - Payment status verified through the backend, or `null` on failure.
 * @param paymentId - FlowRide payment id from the bounce URL.
 * @returns `false` when the attempt could not be loaded (e.g. expired session).
 */
export async function resumePayrexxReturn(
    attemptId: string,
    paymentStatus: PurchasePaymentStatusDTO | null,
    paymentId: string | null,
): Promise<boolean> {
    let resume;

    try {
        resume = await TicketPurchaseApi.getPurchaseAttempt( attemptId );
    } catch {
        return false;
    }

    const secured = paymentStatus !== null && PAYMENT_SECURED_STATUSES.has( paymentStatus );
    const bookingStarted = BOOKING_STARTED_ATTEMPT_STATUSES.has( resume.status );
    const bookingFailed = BOOKING_FAILED_ATTEMPT_STATUSES.has( resume.status );
    const booked = bookingFromResume( resume );
    const defaultClass = persistentSliceStore.peek( pricingPrefsSlice ).categoryOfService === "FIRST_CLASS" ? "1" : "2";
    const selection = rebuildSelectionFromPrebookings( resume.itinerary, resume.prebooking.prebookings, defaultClass );

    /*
     * Once the booking started (or definitively failed) the wizard only displays its
     * outcome: `TicketBookingConfirmation` calls `/book` only from `bookingStatus: "idle"`.
     * A booked attempt whose ticket files are still downloading stays on `verifying`:
     * the mounted confirmation keeps polling until `tickets_fetched`.
     */
    const resolveBookingStatus = ( previous: TicketPurchaseState ): TicketPurchaseState["bookingStatus"] => {
        if ( booked !== null ) {
            return booked.ticketFilesComplete || resume.status === "tickets_fetched" ? "succeeded" : "verifying";
        }
        if ( bookingFailed ) {
            return "failed";
        }
        if ( bookingStarted ) {
            return "verifying";
        }

        return secured ? previous.bookingStatus : "idle";
    };

    const resolveError = (): string | null => {
        if ( bookingFailed ) {
            return "L'achat n'a pas pu aboutir. Aucun billet n'a été émis et aucun montant ne sera débité.";
        }

        return secured || bookingStarted ? null : "Le paiement n'a pas été confirmé. Vous pouvez réessayer.";
    };

    ephemeralStore.setValue( ticketPurchaseSlice, ( previous ): TicketPurchaseState => ( {
        ...previous,
        "isOpen":                true,
        "itinerary":             resume.itinerary,
        "step":                  secured || bookingStarted || bookingFailed ? "confirmation" : "payment",
        "selectionSegmentIndex": 0,
        "selectedOffers":        selection.selectedOffers,
        "segmentClass":          selection.segmentClass,
        "skipSegment":           selection.skipSegment,
        "prebookingResponse":    resume.prebooking,
        "purchaseAttemptId":     resume.purchaseAttemptId,
        "paymentId":             paymentId ?? resume.payment?.paymentId ?? null,
        "payrexxUrl":            null,
        "checkoutMode":          resume.payment?.checkoutMode ?? "standard",

        // The restored mode mirrors an existing payment — the saved-method lookup must not override it.
        "checkoutModeTouched": true,
        "paymentStatus":       paymentStatus ?? "failed",
        "bookingStatus":       resolveBookingStatus( previous ),
        "booking":             booked ?? previous.booking,
        "error":               resolveError(),
        "canClose":            true,
    } ) );

    return true;
}
